import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ProcessMapDoc, ProcessMapEdge, ProcessMapNode, ProcessMapNodeKind } from '../types/global';
import { createEdge, createNode, kindDefaults } from '../lib/processMap';
import { AgentSpecView, hasSpecContent } from './AgentSpecView';

// Editable process-map canvas. Renders nodes + edges in an SVG with a single
// transform group, so pan/zoom are cheap. The toolbar across the top arms add
// modes; clicks on the canvas in that mode insert a node (or, for the edge
// tool, link two nodes). Selection + Delete removes a node and any edges
// touching it. Inline text edit on double-click.
//
// Props mirror FactoryCanvas where it makes sense: `flow`-like doc in, an
// onChange callback the parent uses to surface manual edits to follow-up
// AI turns, plus the optional Make agent / Pin / Close affordances.
type Tool = 'select' | 'pan' | 'box' | 'diamond' | 'circle' | 'text' | 'edge' | 'chevron' | 'document' | 'actor' | 'warning' | 'bracket' | 'milestone';

const VIEW_W_DEFAULT = 1400;
const VIEW_H_DEFAULT = 900;

export function ProcessMapCanvas({
  doc,
  onChange,
  onMakeAgent,
  onPinAgent,
  canMakeAgent,
  canPinAgent,
  onClose,
}: {
  doc: ProcessMapDoc;
  // Called whenever the user manually edits the diagram. The parent persists
  // the latest snapshot and feeds it back to the AI on the next send.
  onChange?: (next: ProcessMapDoc) => void;
  onMakeAgent?: (doc: ProcessMapDoc) => Promise<void> | void;
  onPinAgent?: (doc: ProcessMapDoc) => void;
  canMakeAgent?: boolean;
  canPinAgent?: boolean;
  onClose?: () => void;
}) {
  // Live state for the diagram. Initialised from `doc` and re-synced whenever
  // the AI emits a new one — but local manual edits between AI turns are NOT
  // overwritten by a stale prop. We use a docKey ref to detect AI updates.
  const [nodes, setNodes] = useState<ProcessMapNode[]>(doc.nodes);
  const [edges, setEdges] = useState<ProcessMapEdge[]>(doc.edges);
  const [title, setTitle] = useState(doc.title);
  const [description, setDescription] = useState(doc.description || '');
  const aiDocSigRef = useRef<string>('');

  // Re-sync from props when the AI emits a new doc. We hash the incoming doc
  // and only replace local state if the hash actually changed — this prevents
  // a parent re-render that doesn't change the AI's diagram from wiping the
  // user's in-progress manual edits.
  useEffect(() => {
    const sig = `${doc.title}|${doc.nodes.length}|${doc.edges.length}|${JSON.stringify(doc.nodes.map((n) => [n.id, n.text, n.x, n.y]))}`;
    if (sig === aiDocSigRef.current) return;
    aiDocSigRef.current = sig;
    setNodes(doc.nodes);
    setEdges(doc.edges);
    setTitle(doc.title);
    setDescription(doc.description || '');
  }, [doc]);

  // Surface every local change to the parent so it can primer-prefix the next
  // user message with the current map state. Skip the very first emit (the
  // one triggered by setting initial state from the AI's doc).
  const firstEmitRef = useRef(true);
  useEffect(() => {
    if (firstEmitRef.current) { firstEmitRef.current = false; return; }
    onChange?.({ kind: 'process-map', title, description: description || undefined, nodes, edges, spec: doc.spec });
  }, [nodes, edges, title, description, onChange, doc.spec]);

  const [tool, setTool] = useState<Tool>('select');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [edgeFrom, setEdgeFrom] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  // When the agent-factory turn ran GPT-image in parallel with the JSON
  // diagram, doc.imageUrl is set and the toolbar shows a Diagram | Visual
  // toggle. Default to "diagram" so the editable SVG is the first thing the
  // user sees; reset to "diagram" if the image disappears between turns.
  const imageUrl = doc.imageUrl?.trim() || '';
  const [view, setView] = useState<'diagram' | 'visual'>('diagram');
  useEffect(() => { if (!imageUrl) setView('diagram'); }, [imageUrl]);
  // Make-agent button feedback. On mobile the diagram is a full-screen overlay
  // covering the chat, so the kickoff happening in the panel behind it is
  // invisible — the inline spinner/✓ is the only signal the click landed.
  const [isMaking, setIsMaking] = useState(false);
  const [makeSuccess, setMakeSuccess] = useState(false);
  // The written agent brief travels on the AI's doc (doc.spec); it isn't a
  // hand-editable canvas property, so we read it straight off the latest prop.
  const [showSpec, setShowSpec] = useState(false);
  const specAvailable = hasSpecContent(doc.spec);
  useEffect(() => {
    if (!specAvailable) setShowSpec(false);
  }, [specAvailable]);

  const svgRef = useRef<SVGSVGElement | null>(null);

  // Convert client-space (mouse event coords) into diagram-space coords by
  // undoing the SVG's CTM. Works regardless of zoom/pan + browser DPR.
  const toDiagramCoords = useCallback((evt: { clientX: number; clientY: number }) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const local = pt.matrixTransform(ctm.inverse());
    return { x: (local.x - pan.x) / zoom, y: (local.y - pan.y) / zoom };
  }, [pan.x, pan.y, zoom]);

  // --- Tool handlers ------------------------------------------------------

  const handleCanvasMouseDown = useCallback((evt: React.MouseEvent<SVGSVGElement>) => {
    if (editingId) return;
    const target = evt.target as Element | null;
    const onNode = target?.closest('[data-node-id]') != null;
    if (onNode) return;

    if (tool === 'pan' || (tool === 'select' && evt.button === 1) || (tool === 'select' && evt.shiftKey)) {
      // Drag-to-pan. Track delta until mouseup.
      const startX = evt.clientX - pan.x;
      const startY = evt.clientY - pan.y;
      const move = (e: MouseEvent) => setPan({ x: e.clientX - startX, y: e.clientY - startY });
      const up = () => {
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup', up);
      };
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);
      return;
    }

    if (tool === 'select') {
      setSelectedId(null);
      setEdgeFrom(null);
      return;
    }

    if (tool === 'edge') {
      // Click on empty cancels the in-progress edge.
      setEdgeFrom(null);
      return;
    }

    const { x, y } = toDiagramCoords(evt);
    const kind: ProcessMapNodeKind = tool === 'text' ? 'text' : tool;
    const newNode = createNode(
      kind,
      x,
      y,
      kind === 'text' ? 'Text' :
      kind === 'diamond' ? 'Decision?' :
      kind === 'actor' ? 'Actor' :
      kind === 'document' ? 'Doc' :
      kind === 'warning' ? 'Warning' :
      kind === 'bracket' ? 'Bracket' :
      kind === 'milestone' ? 'Milestone' : 'Step'
    );
    setNodes((ns) => [...ns, newNode]);
    setSelectedId(newNode.id);
    setEditingId(newNode.id);
    // Return to select after placing one node — feels more natural than
    // continuing in placement mode.
    setTool('select');
  }, [editingId, pan.x, pan.y, toDiagramCoords, tool]);

  const handleNodeClick = useCallback((nodeId: string, evt: React.MouseEvent) => {
    evt.stopPropagation();
    if (editingId && editingId !== nodeId) setEditingId(null);
    if (tool === 'edge') {
      if (edgeFrom == null) {
        setEdgeFrom(nodeId);
      } else if (edgeFrom !== nodeId) {
        setEdges((es) => [...es, createEdge(edgeFrom, nodeId)]);
        setEdgeFrom(null);
        setTool('select');
      }
      return;
    }
    setSelectedId(nodeId);
  }, [edgeFrom, editingId, tool]);

  const handleNodeDoubleClick = useCallback((nodeId: string, evt: React.MouseEvent) => {
    evt.stopPropagation();
    setEditingId(nodeId);
    setSelectedId(nodeId);
  }, []);

  const handleNodeMouseDown = useCallback((nodeId: string, evt: React.MouseEvent) => {
    if (tool !== 'select') return;
    if (editingId === nodeId) return;
    evt.stopPropagation();
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;
    const origin = toDiagramCoords(evt);
    const offsetX = origin.x - node.x;
    const offsetY = origin.y - node.y;
    let didMove = false;
    const move = (e: MouseEvent) => {
      const p = toDiagramCoords(e);
      const nx = Math.round(p.x - offsetX);
      const ny = Math.round(p.y - offsetY);
      didMove = true;
      setNodes((ns) => ns.map((n) => (n.id === nodeId ? { ...n, x: nx, y: ny } : n)));
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      if (!didMove) setSelectedId(nodeId);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }, [editingId, nodes, toDiagramCoords, tool]);

  // Mouse wheel zooms around the cursor. We compute the diagram-space point
  // under the cursor before and after the zoom change and shift the pan to
  // keep that point stationary on screen.
  const handleWheel = useCallback((evt: React.WheelEvent<SVGSVGElement>) => {
    evt.preventDefault();
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const cx = evt.clientX - rect.left;
    const cy = evt.clientY - rect.top;
    const factor = Math.exp(-evt.deltaY * 0.0015);
    setZoom((z) => {
      const nextZoom = Math.min(2.5, Math.max(0.25, z * factor));
      setPan((p) => ({
        x: cx - ((cx - p.x) * (nextZoom / z)),
        y: cy - ((cy - p.y) * (nextZoom / z)),
      }));
      return nextZoom;
    });
  }, []);

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    setNodes((ns) => ns.filter((n) => n.id !== selectedId));
    setEdges((es) => es.filter((e) => e.from !== selectedId && e.to !== selectedId));
    setSelectedId(null);
    setEditingId(null);
  }, [selectedId]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (editingId) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        // Only trigger when the canvas (not the iframe / chat) has focus —
        // otherwise the global Backspace would eat the user's typing in the
        // composer. We approximate this by checking the target is the body
        // or the canvas itself.
        const t = e.target as HTMLElement | null;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
        e.preventDefault();
        deleteSelected();
      }
      if (e.key === 'Escape') {
        setEditingId(null);
        setEdgeFrom(null);
        setTool('select');
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deleteSelected, editingId, selectedId]);

  const fitToView = useCallback(() => {
    if (!nodes.length) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
      return;
    }
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of nodes) {
      minX = Math.min(minX, n.x);
      minY = Math.min(minY, n.y);
      maxX = Math.max(maxX, n.x + n.w);
      maxY = Math.max(maxY, n.y + n.h);
    }
    const pad = 80;
    const dw = (maxX - minX) + pad * 2;
    const dh = (maxY - minY) + pad * 2;
    const zX = rect.width / dw;
    const zY = rect.height / dh;
    const z = Math.min(2, Math.max(0.25, Math.min(zX, zY)));
    setZoom(z);
    setPan({
      x: (rect.width - dw * z) / 2 - (minX - pad) * z,
      y: (rect.height - dh * z) / 2 - (minY - pad) * z,
    });
  }, [nodes]);

  // Auto-fit on first render so the AI's freshly-emitted diagram lands in
  // view rather than potentially off-screen for unusual coordinates.
  useEffect(() => { fitToView(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const nodesById = useMemo(() => {
    const map = new Map<string, ProcessMapNode>();
    for (const n of nodes) map.set(n.id, n);
    return map;
  }, [nodes]);

  const handleTextCommit = useCallback((nodeId: string, value: string) => {
    setNodes((ns) => ns.map((n) => (n.id === nodeId ? { ...n, text: value } : n)));
  }, []);

  const liveDoc: ProcessMapDoc = useMemo(
    () => ({ kind: 'process-map', title, description: description || undefined, nodes, edges, spec: doc.spec }),
    [title, description, nodes, edges, doc.spec],
  );

  const handleMakeClick = async () => {
    if (!onMakeAgent || isMaking) return;
    setIsMaking(true);
    setMakeSuccess(false);
    try {
      await onMakeAgent(liveDoc);
      setMakeSuccess(true);
      setTimeout(() => setMakeSuccess(false), 3000);
    } catch (err) {
      console.error(err);
    } finally {
      setIsMaking(false);
    }
  };

  return (
    <section className="process-map-canvas">
      <header className="pm-toolbar">
        <div className="pm-toolbar-title">
          <input
            className="pm-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Process map title"
          />
        </div>
        <div className="pm-tools">
          <ToolButton tool="select" current={tool} setTool={setTool} icon="fa-mouse-pointer" label="Select" />
          <ToolButton tool="pan" current={tool} setTool={setTool} icon="fa-hand-paper" label="Pan" />
          <span className="pm-sep" />
          <ToolButton tool="box" current={tool} setTool={setTool} icon="fa-square" label="Box" />
          <ToolButton tool="chevron" current={tool} setTool={setTool} icon="fa-chevron-right" label="Chevron" />
          <ToolButton tool="diamond" current={tool} setTool={setTool} icon="fa-diamond" label="Diamond" />
          <ToolButton tool="circle" current={tool} setTool={setTool} icon="fa-circle" label="Circle" />
          <ToolButton tool="text" current={tool} setTool={setTool} icon="fa-font" label="Text" />
          <ToolButton tool="actor" current={tool} setTool={setTool} icon="fa-user" label="Actor" />
          <ToolButton tool="document" current={tool} setTool={setTool} icon="fa-file-alt" label="Document" />
          <ToolButton tool="warning" current={tool} setTool={setTool} icon="fa-exclamation-triangle" label="Warning" />
          <ToolButton tool="bracket" current={tool} setTool={setTool} icon="fa-sticky-note" label="Bracket" />
          <ToolButton tool="milestone" current={tool} setTool={setTool} icon="fa-cube" label="Milestone" />
          <ToolButton tool="edge" current={tool} setTool={setTool} icon="fa-arrow-right" label="Edge" />
          <span className="pm-sep" />
          <button type="button" className="pm-tool" onClick={fitToView} title="Fit to view">
            <i className="fas fa-expand" aria-hidden="true" />
          </button>
          <button type="button" className="pm-tool" onClick={() => setZoom((z) => Math.min(2.5, z * 1.2))} title="Zoom in">
            <i className="fas fa-search-plus" aria-hidden="true" />
          </button>
          <button type="button" className="pm-tool" onClick={() => setZoom((z) => Math.max(0.25, z / 1.2))} title="Zoom out">
            <i className="fas fa-search-minus" aria-hidden="true" />
          </button>
          <span className="pm-zoom">{Math.round(zoom * 100)}%</span>
          {selectedId ? (
            <button type="button" className="pm-tool pm-danger" onClick={deleteSelected} title="Delete selected">
              <i className="fas fa-trash" aria-hidden="true" />
            </button>
          ) : null}
        </div>
        <div className="pm-actions">
          {imageUrl ? (
            <div className="pm-view-tabs" role="tablist" aria-label="Diagram view">
              <button
                type="button"
                role="tab"
                aria-selected={view === 'diagram'}
                className={`pm-view-tab${view === 'diagram' ? ' on' : ''}`}
                onClick={() => setView('diagram')}
                title="Editable process map"
              >
                <i className="fas fa-sitemap" aria-hidden="true" /> Diagram
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === 'visual'}
                className={`pm-view-tab${view === 'visual' ? ' on' : ''}`}
                onClick={() => setView('visual')}
                title="ChatGPT Image visualization of the same workflow"
              >
                <i className="fas fa-image" aria-hidden="true" /> Visual
              </button>
            </div>
          ) : null}
          {specAvailable ? (
            <button
              type="button"
              className="pm-close pm-brief"
              onClick={() => setShowSpec(true)}
              title="View the agent brief — your request, a clarified rewrite, and a detailed spec"
              aria-label="View the agent brief"
            >
              <i className="fas fa-clipboard-list" aria-hidden="true" />
            </button>
          ) : null}
          {canMakeAgent && onMakeAgent ? (
            <button
              type="button"
              className={`pm-action primary${makeSuccess ? ' success' : ''}`}
              onClick={handleMakeClick}
              disabled={isMaking}
              title="Implement this agent: pin it, dispatch an Alty Dev turn to write the impl doc + any net-new code, and rename the chat after the diagram title"
            >
              {isMaking ? (
                <><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Making agent...</>
              ) : makeSuccess ? (
                <><i className="fas fa-check-circle" aria-hidden="true" /> Agent made!</>
              ) : (
                <><i className="fas fa-check" aria-hidden="true" /> Make agent</>
              )}
            </button>
          ) : null}
          {canPinAgent && onPinAgent ? (
            <button type="button" className="pm-action" onClick={() => onPinAgent(liveDoc)}>
              <i className="fas fa-thumbtack" aria-hidden="true" /> Pin
            </button>
          ) : null}
          {onClose ? (
            <button type="button" className="pm-close" onClick={onClose} title="Close" aria-label="Close">
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </header>

      {view === 'diagram' && edgeFrom ? (
        <div className="pm-hint">Edge mode: click the target node (Esc to cancel)</div>
      ) : view === 'diagram' && tool !== 'select' && tool !== 'pan' ? (
        <div className="pm-hint">Click on the canvas to place a {tool} (Esc to cancel)</div>
      ) : null}

      {view === 'visual' && imageUrl ? (
        <figure className="pm-visual">
          <img src={imageUrl} alt={`Visual rendering of ${title || 'process map'}`} />
        </figure>
      ) : null}

      <svg
        ref={svgRef}
        className="pm-svg"
        width={VIEW_W_DEFAULT}
        height={VIEW_H_DEFAULT}
        onMouseDown={handleCanvasMouseDown}
        onWheel={handleWheel}
        style={{
          cursor: tool === 'pan' ? 'grab' : tool === 'edge' ? 'crosshair' : tool !== 'select' ? 'crosshair' : 'default',
          ...(view === 'visual' ? { display: 'none' } : {}),
        }}
      >
        <defs>
          <marker id="pm-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="9" markerHeight="9" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#16140f" />
          </marker>
          <marker id="pm-arrow-orange" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="9" markerHeight="9" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#f28f3b" />
          </marker>
          <marker id="pm-arrow-teal" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="9" markerHeight="9" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#3db8b8" />
          </marker>
          <marker id="pm-arrow-grey" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="9" markerHeight="9" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#8c8c8c" />
          </marker>
          <pattern id="pm-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#ebe8e0" strokeWidth="1" />
          </pattern>
        </defs>
        <rect x={-5000} y={-5000} width={10000} height={10000} fill="url(#pm-grid)"
              transform={`translate(${pan.x},${pan.y}) scale(${zoom})`} />
        <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
          {edges.map((edge) => {
            const a = nodesById.get(edge.from);
            const b = nodesById.get(edge.to);
            if (!a || !b) return null;
            return <Edge key={edge.id} a={a} b={b} edge={edge} />;
          })}
          {nodes.map((node) => (
            <Node
              key={node.id}
              node={node}
              selected={node.id === selectedId}
              editing={node.id === editingId}
              edgePending={node.id === edgeFrom}
              onMouseDown={(evt) => handleNodeMouseDown(node.id, evt)}
              onClick={(evt) => handleNodeClick(node.id, evt)}
              onDoubleClick={(evt) => handleNodeDoubleClick(node.id, evt)}
              onTextCommit={(value) => handleTextCommit(node.id, value)}
              onTextBlur={() => setEditingId(null)}
            />
          ))}
        </g>
      </svg>

      {description ? (
        <footer className="pm-footer"><span>{description}</span></footer>
      ) : null}

      {showSpec && doc.spec ? (
        <AgentSpecView spec={doc.spec} onBack={() => setShowSpec(false)} />
      ) : null}
    </section>
  );
}

function ToolButton({ tool, current, setTool, icon, label }: {
  tool: Tool;
  current: Tool;
  setTool: (t: Tool) => void;
  icon: string;
  label: string;
}) {
  const active = tool === current;
  return (
    <button
      type="button"
      className={`pm-tool ${active ? 'on' : ''}`}
      onClick={() => setTool(tool)}
      title={label}
      aria-label={label}
    >
      <i className={`fas ${icon}`} aria-hidden="true" />
    </button>
  );
}

function Node({
  node, selected, editing, edgePending,
  onMouseDown, onClick, onDoubleClick, onTextCommit, onTextBlur,
}: {
  node: ProcessMapNode;
  selected: boolean;
  editing: boolean;
  edgePending: boolean;
  onMouseDown: (evt: React.MouseEvent) => void;
  onClick: (evt: React.MouseEvent) => void;
  onDoubleClick: (evt: React.MouseEvent) => void;
  onTextCommit: (value: string) => void;
  onTextBlur: () => void;
}) {
  const isCustomIcon = node.kind === 'document' || node.kind === 'actor' || node.kind === 'warning' || node.kind === 'milestone';
  const stroke = selected ? '#4a6cf7' : edgePending ? '#d99e00' : node.stroke || '#16140f';
  const strokeWidth = selected || edgePending ? 2.5 : 1.6;
  const fill = node.fill || fillForKind(node.kind);
  const cx = node.x + node.w / 2;
  const cy = node.y + node.h / 2;
  const dims = kindDefaults(node.kind);
  const w = node.w || dims.w;
  const h = node.h || dims.h;

  const selectionOutline = selected ? (
    <rect x={node.x} y={node.y} width={w} height={h} rx={6} ry={6}
          fill="transparent" stroke="#4a6cf7" strokeWidth={1.5} strokeDasharray="4 4" style={{ pointerEvents: 'none' }} />
  ) : null;

  const shape = (() => {
    switch (node.kind) {
      case 'circle':
      case 'start':
      case 'end': {
        const rx = w / 2;
        const ry = h / 2;
        return (
          <ellipse cx={cx} cy={cy} rx={rx} ry={ry}
                   fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
        );
      }
      case 'diamond': {
        const pts = `${cx},${node.y} ${node.x + w},${cy} ${cx},${node.y + h} ${node.x},${cy}`;
        return (
          <g>
            <polygon points={pts} fill="#eef2ff" stroke={selected ? '#4a6cf7' : '#3db8b8'} strokeWidth={strokeWidth} />
            <circle cx={cx} cy={cy} r={14} fill="#eef2ff" stroke="#3db8b8" strokeWidth={2} />
            <circle cx={cx} cy={cy} r={6} fill="#3db8b8" />
          </g>
        );
      }
      case 'chevron': {
        const pts = `${node.x},${node.y} ${node.x + w - 15},${node.y} ${node.x + w},${cy} ${node.x + w - 15},${node.y + h} ${node.x},${node.y + h}`;
        return <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
      }
      case 'document': {
        return (
          <g>
            <polygon
              points={`${cx - 14},${node.y} ${cx + 6},${node.y} ${cx + 14},${node.y + 8} ${cx + 14},${node.y + 34} ${cx - 14},${node.y + 34}`}
              fill="#595959"
              stroke={selected ? '#4a6cf7' : '#404040'}
              strokeWidth={1.8}
            />
            <polygon
              points={`${cx + 6},${node.y} ${cx + 6},${node.y + 8} ${cx + 14},${node.y + 8}`}
              fill="#ffffff"
              stroke={selected ? '#4a6cf7' : '#404040'}
              strokeWidth={1.8}
            />
            <line x1={cx - 8} y1={node.y + 16} x2={cx + 8} y2={node.y + 16} stroke="#ffffff" strokeWidth={1.8} strokeLinecap="round" />
            <line x1={cx - 8} y1={node.y + 24} x2={cx + 8} y2={node.y + 24} stroke="#ffffff" strokeWidth={1.8} strokeLinecap="round" />
            {selectionOutline}
          </g>
        );
      }
      case 'actor': {
        return (
          <g>
            <path
              d={`M ${cx - 14} ${node.y + 34} A 14 14 0 0 1 ${cx + 14} ${node.y + 34} Z`}
              fill="#e89a3e"
              stroke={selected ? '#4a6cf7' : '#d0822d'}
              strokeWidth={1.5}
            />
            <circle
              cx={cx}
              cy={node.y + 14}
              r={7}
              fill="#e89a3e"
              stroke={selected ? '#4a6cf7' : '#d0822d'}
              strokeWidth={1.5}
            />
            {selectionOutline}
          </g>
        );
      }
      case 'warning': {
        return (
          <g>
            <circle
              cx={cx}
              cy={node.y + 18}
              r={12}
              fill="transparent"
              stroke={selected ? '#4a6cf7' : '#e6a100'}
              strokeWidth={2.5}
            />
            <path
              d={`M ${cx} ${node.y + 11} L ${cx} ${node.y + 20}`}
              stroke={selected ? '#4a6cf7' : '#e6a100'}
              strokeWidth={3}
              strokeLinecap="round"
            />
            <circle
              cx={cx}
              cy={node.y + 25}
              r={1.6}
              fill={selected ? '#4a6cf7' : '#e6a100'}
            />
            {selectionOutline}
          </g>
        );
      }
      case 'milestone': {
        return (
          <g>
            <rect
              x={cx - 16}
              y={node.y + 4}
              width={32}
              height={32}
              rx={4}
              ry={4}
              fill="#595959"
              stroke={selected ? '#4a6cf7' : '#404040'}
              strokeWidth={1.8}
            />
            {selectionOutline}
          </g>
        );
      }
      case 'bracket': {
        return (
          <g>
            <path
              d={`M ${node.x + 12} ${node.y} L ${node.x} ${node.y} L ${node.x} ${node.y + h} L ${node.x + 12} ${node.y + h}`}
              fill="none"
              stroke={selected ? '#4a6cf7' : '#595959'}
              strokeWidth={2.5}
            />
            {selectionOutline}
          </g>
        );
      }
      case 'text':
        return null; // text-only — no border or fill
      default:
        return (
          <rect x={node.x} y={node.y} width={w} height={h} rx={10} ry={10}
                fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
        );
    }
  })();

  return (
    <g
      data-node-id={node.id}
      onMouseDown={onMouseDown}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      style={{ cursor: 'move' }}
    >
      {shape}
      {editing ? (
        <foreignObject
          x={node.x + 4}
          y={node.y + (isCustomIcon ? 44 : 4)}
          width={w - 8}
          height={h - (isCustomIcon ? 48 : 8)}
        >
          <textarea
            autoFocus
            defaultValue={node.text}
            className="pm-node-edit"
            onChange={(e) => onTextCommit(e.currentTarget.value)}
            onBlur={onTextBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onTextBlur();
              }
            }}
          />
        </foreignObject>
      ) : (
        <NodeLabel x={cx} y={cy} w={w} h={h} text={node.text} kind={node.kind} />
      )}
    </g>
  );
}

function NodeLabel({ x, y, w, h, text, kind }: {
  x: number; y: number; w: number; h: number; text: string; kind: ProcessMapNodeKind;
}) {
  const isCustomIcon = kind === 'document' || kind === 'actor' || kind === 'warning' || kind === 'milestone';
  const isBracket = kind === 'bracket';

  // Wrap at roughly w / 8 chars. Cap at 4 lines, ellipsise the rest.
  const padding = kind === 'diamond' ? 28 : 14;
  const usableW = isBracket ? Math.max(40, w - 24) : Math.max(40, w - padding * 2);
  const charsPerLine = Math.max(8, Math.floor(usableW / 7.5));
  const words = (text || '').split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    if (!line.length) line = word;
    else if ((line + ' ' + word).length <= charsPerLine) line += ' ' + word;
    else { lines.push(line); line = word; }
    if (lines.length >= 4) break;
  }
  if (line && lines.length < 4) lines.push(line);
  
  const lineHeight = 16;
  let startY: number;
  let textX = x;
  let textAnchor: 'middle' | 'start' = 'middle';

  if (isCustomIcon) {
    // Upper icon is roughly 40px tall. Place label text centered in remaining height below.
    const nodeY = y - h / 2;
    startY = nodeY + 44 + ((h - 44) - (lines.length - 1) * lineHeight) / 2 + 6;
  } else if (isBracket) {
    // Align left, indented past the bracket bracket-line
    const nodeX = x - w / 2;
    textX = nodeX + 16;
    textAnchor = 'start';
    startY = y - ((lines.length - 1) * lineHeight) / 2;
  } else {
    startY = y - ((lines.length - 1) * lineHeight) / 2;
  }

  return (
    <text
      x={textX}
      y={startY}
      textAnchor={textAnchor}
      dominantBaseline="middle"
      fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
      fontSize={kind === 'text' || isBracket ? 14 : 13}
      fontWeight={kind === 'text' ? 500 : 600}
      fill="#16140f"
      style={{ pointerEvents: 'none', userSelect: 'none' }}
    >
      {lines.map((ln, i) => (
        <tspan key={i} x={textX} dy={i === 0 ? 0 : lineHeight}>{ln}</tspan>
      ))}
    </text>
  );
}

function Edge({ a, b, edge }: { a: ProcessMapNode; b: ProcessMapNode; edge: ProcessMapEdge }) {
  // Trim the line so it touches each node's bounding rect rather than its
  // centre — gives the arrow a natural attach point.
  const start = edgePoint(a, b);
  const end = edgePoint(b, a);
  const dash = edge.dashed ? '6 4' : undefined;

  let strokeColor = '#f28f3b';
  let markerId = 'pm-arrow-orange';

  if (a.kind === 'actor') {
    strokeColor = '#3db8b8';
    markerId = 'pm-arrow-teal';
  } else if (a.kind === 'warning') {
    strokeColor = '#8c8c8c';
    markerId = 'pm-arrow-grey';
  }

  return (
    <g pointerEvents="none">
      <line
        x1={start.x} y1={start.y} x2={end.x} y2={end.y}
        stroke={strokeColor} strokeWidth={2} markerEnd={`url(#${markerId})`}
        strokeDasharray={dash}
      />
      {edge.label ? (
        <g>
          <rect
            x={(start.x + end.x) / 2 - edgeLabelWidth(edge.label) / 2}
            y={(start.y + end.y) / 2 - 11}
            width={edgeLabelWidth(edge.label)} height={22} rx={10} ry={10}
            fill="#fff" stroke="#d8d6cf" strokeWidth={1}
          />
          <text
            x={(start.x + end.x) / 2}
            y={(start.y + end.y) / 2 + 4}
            textAnchor="middle"
            fontSize={12} fill="#54504a"
            fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
          >{edge.label}</text>
        </g>
      ) : null}
    </g>
  );
}

function edgeLabelWidth(label: string): number {
  return Math.max(36, label.length * 7 + 14);
}

// Intersection of the line A→B with A's rectangle/ellipse, returning the
// closest point on A's perimeter. For ellipses we sample analytically; for
// rects we clip the centre-to-centre line against the rect's bounds.
function edgePoint(a: ProcessMapNode, b: ProcessMapNode): { x: number; y: number } {
  const acx = a.x + a.w / 2;
  const acy = a.y + a.h / 2;
  const bcx = b.x + b.w / 2;
  const bcy = b.y + b.h / 2;
  const dx = bcx - acx;
  const dy = bcy - acy;
  if (dx === 0 && dy === 0) return { x: acx, y: acy };
  const isEllipse = a.kind === 'circle' || a.kind === 'start' || a.kind === 'end';
  if (isEllipse) {
    const rx = a.w / 2;
    const ry = a.h / 2;
    const t = 1 / Math.sqrt((dx * dx) / (rx * rx) + (dy * dy) / (ry * ry));
    return { x: acx + dx * t, y: acy + dy * t };
  }
  if (a.kind === 'diamond') {
    // Diamond: |x'/rx| + |y'/ry| = 1 → t = 1 / (|dx|/rx + |dy|/ry)
    const rx = a.w / 2;
    const ry = a.h / 2;
    const t = 1 / (Math.abs(dx) / rx + Math.abs(dy) / ry);
    return { x: acx + dx * t, y: acy + dy * t };
  }
  // Rect / text — clip the ray against the AABB.
  const halfW = a.w / 2;
  const halfH = a.h / 2;
  const tx = halfW / Math.abs(dx || 1e-6);
  const ty = halfH / Math.abs(dy || 1e-6);
  const t = Math.min(tx, ty);
  return { x: acx + dx * t, y: acy + dy * t };
}

function fillForKind(kind: ProcessMapNodeKind): string {
  switch (kind) {
    case 'start': return '#dff7e3';
    case 'end': return '#fce6e4';
    case 'diamond': return '#fff7d6';
    case 'circle': return '#eef2ff';
    case 'text': return 'transparent';
    case 'chevron': return '#ebe9e4';
    case 'document':
    case 'actor':
    case 'warning':
    case 'bracket':
    case 'milestone':
      return 'transparent';
    default: return '#ffffff';
  }
}
